import React from "react";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemText from "@material-ui/core/ListItemText";
import TextField from "@material-ui/core/TextField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import {
  getDataTypeByPath,
  getRelation,
  normalisePathParts,
  LinkedRootDataType,
} from "containers/Settings/RuleBooks/Schema/schema";
import {
  FieldType,
  FieldValueTypeDisplayNameMap,
  FieldValueTypesByFieldType,
} from "containers/Settings/RuleBooks/constants";

export interface RelationDataTypeMenuItemProps {
  dataTour: string;

  // The current depth of the menu, used to limit the number of levels deep the menu will display
  depth: number;

  // The path from the root of the schema to use as a prefix for the via attribe
  fieldPath: string;

  // A filter to apply to determine which properties of this datatype are displayed
  fieldType?: ValueInObj<typeof FieldType> | null;

  // The path relative to the field path this datatype represent
  via: string;

  // Used in conjunction with allowSelectManyRelations to determine if the user can select this item
  isManyRelation?: boolean;

  // The used to retrieve the relationship between the parent datatype and this datatype,
  // relative to the path yielded by combining fieldPath and via
  relationVia: string;

  // Used in conjunction with showManyRelations to determine if the user can select this datatype
  allowSelectRelations?: boolean;

  // Used in conjunction with showManyRelations to determine if the user can select this datatype
  allowSelectManyRelations?: boolean;

  schema: LinkedRootDataType;

  // Used to determine if the sub-menu should show the many-to-one relations of this datatype
  showRelations?: boolean;

  // Used to determine if the sub-menu should show the many-to-many and many-to-one relations of this datatype
  showManyRelations?: boolean;

  // Used to determine if the sub-menu should show the properties of this datatype
  showProperties?: boolean;

  // The function to call when the user selects a new path, this path is relative to the value of fieldPath
  onSelect: (newPath: string) => void;
}

export function RelationDataTypeMenuItem(props: RelationDataTypeMenuItemProps) {
  const {
    dataTour,
    depth,
    onSelect,
    fieldPath,
    fieldType,
    via,
    isManyRelation,
    relationVia,
    allowSelectRelations,
    showRelations,
    allowSelectManyRelations,
    schema,
    showManyRelations,
    showProperties,
  } = props;

  const [anchorEl, setAnchorEl] = React.useState(null);

  const dataType = getDataTypeByPath(
    schema,
    normalisePathParts([fieldPath, via]),
  );

  const relation = getRelation(dataType, relationVia);

  const onSubSelect = newValue => {
    setAnchorEl(null);
    onSelect(newValue);
  };

  const onSubClose = () => {
    setAnchorEl(null);
  };

  const canSelect =
    (isManyRelation && allowSelectManyRelations) ||
    (!isManyRelation && allowSelectRelations);

  function onClickMenuItem(event) {
    if (canSelect) {
      onSelect(normalisePathParts([via, relation?.via]));
    } else {
      setAnchorEl(event.target);
    }
  }

  return (
    <>
      <MenuItem
        selected={via === relation?.via}
        onClick={onClickMenuItem}
        data-tour={`${dataTour}_DataTypeMenuItem_${relation?.via}`}
      >
        <ListItemText
          primary={relation.name}
          secondary={relation.dataType?.singular}
        />
        {!canSelect && <FontAwesomeIcon icon={faChevronRight} />}
      </MenuItem>
      { }
      <DataTypeMenu
        dataTour={dataTour}
        pathPart={relation.via}
        depth={depth + 1}
        anchorEl={anchorEl}
        onClose={onSubClose}
        onSelect={onSubSelect}
        fieldType={fieldType}
        via={normalisePathParts([via, relation.via])}
        fieldPath={fieldPath}
        allowSelectManyRelations={allowSelectManyRelations}
        allowSelectRelations={allowSelectRelations}
        schema={schema}
        showManyRelations={showManyRelations && depth <= 2}
        showProperties={showProperties}
        showRelations={showRelations && depth <= 2}
      />
    </>
  );
}

function getFieldTypeMapList(fieldType) {
  return FieldValueTypesByFieldType[fieldType] || [];
}

function clickMenuItemHandler(property, via, onSelect) {
  return () => onSelect(normalisePathParts([via, property.via]));
}

export interface DataTypeMenuProps
  extends Omit<
    RelationDataTypeMenuItemProps,
    "isManyRelation" | "relationVia"
  > {
  // Called as a request to close the menu
  onClose: () => void;

  // A HTML element to provide positioning information when spawning the menu (when open)
  anchorEl: HTMLElement | null;

  // The part of the path this menu is responsible for
  // e.g. when fieldPath is $.lots[*], and the datasources value is `consignment.vendor.vendor_property.pic`
  // this menu could be responsible for rendering any one of consignment, vendor, or vendor_property menus
  pathPart?: string;

  // block editing of the fields
  readOnly?: boolean;
}

export function DataTypeMenu(props: DataTypeMenuProps) {
  const {
    allowSelectManyRelations,
    allowSelectRelations,
    anchorEl,
    dataTour,
    depth,
    fieldPath,
    fieldType,
    onClose,
    onSelect,
    pathPart,
    schema,
    showManyRelations,
    showProperties,
    showRelations,
    via,
    readOnly,
  } = props;

  const dataTypes = getFieldTypeMapList(fieldType);

  const dataType = getDataTypeByPath(
    schema,
    normalisePathParts([fieldPath, via]),
  );
  const [customPath, setCustomPath] = React.useState(dataType ? "" : pathPart);

  const allProperties = dataType ? dataType.properties : [];
  const relations = dataType ? dataType.relations : [];
  const manyRelations = dataType ? dataType.manyRelations : [];

  const properties = allProperties.filter(
    property =>
      !fieldType || property.type === null || dataTypes.includes(property.type),
  );

  function onKeyDownCustomFieldInput(event) {
    // Prevent losing focus due to the menu capturing the key event
    event.stopPropagation();
    if (event.key === "Enter") {
      onClose();
      onSelect(normalisePathParts([via, customPath]));
      setCustomPath("");
    }
  }

  function onBlurCustomFieldInput() {
    onClose();
    onSelect(normalisePathParts([via, customPath]));
    setCustomPath("");
  }

  function onChangeCustomFieldInput(event) {
    setCustomPath(event.target.value);
  }

  return (
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
      {showProperties &&
        properties.map(property => (
          <MenuItem
            key={property.via}
            selected={via === property.via}
            onClick={clickMenuItemHandler(property, via, onSelect)}
            data-tour={`${dataTour}_DataTypeMenu_${property.via}`}
            disabled={readOnly}
          >
            <ListItemText
              primary={property.displayName || property.name}
              secondary={FieldValueTypeDisplayNameMap[property.type] || ""}
            />
          </MenuItem>
        ))}
      {showRelations &&
        depth <= 2 &&
        relations.map(relation => (
          <RelationDataTypeMenuItem
            dataTour={dataTour}
            key={relation.via}
            depth={depth}
            onSelect={onSelect}
            fieldType={fieldType}
            fieldPath={fieldPath}
            via={via}
            relationVia={relation.via}
            allowSelectManyRelations={allowSelectManyRelations}
            allowSelectRelations={allowSelectRelations}
            schema={schema}
            showManyRelations={showManyRelations}
            showProperties={showProperties}
            showRelations={showRelations}
          />
        ))}
      {showManyRelations &&
        depth <= 2 &&
        manyRelations.map(manyRelation => (
          <RelationDataTypeMenuItem
            dataTour={dataTour}
            key={manyRelation.via}
            depth={depth}
            onSelect={onSelect}
            fieldType={fieldType}
            fieldPath={fieldPath}
            isManyRelation
            via={via}
            relationVia={manyRelation.via}
            allowSelectRelations={allowSelectRelations}
            allowSelectManyRelations={allowSelectManyRelations}
            schema={schema}
            showRelations={showRelations}
            showManyRelations={showManyRelations}
            showProperties={showProperties}
          />
        ))}
      <MenuItem>
        <TextField
          type="text"
          onKeyDown={onKeyDownCustomFieldInput}
          onBlur={onBlurCustomFieldInput}
          onChange={onChangeCustomFieldInput}
          placeholder="Add a new relation"
          value={customPath}
        />
      </MenuItem>
    </Menu>
  );
}
